import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { LucideAngularModule, User, Lock, AlertCircle, Loader, Layers } from 'lucide-angular';

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        LucideAngularModule
    ],
    providers: [{ provide: LucideAngularModule, useValue: LucideAngularModule.pick({ User, Lock, AlertCircle, Loader, Layers }) }],
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css']
})
export class LoginComponent {
    authService = inject(AuthService);
    router = inject(Router);
    fb = new FormBuilder();

    // We can still expose them for [img] binding if we want, or use string names.
    // If using .pick, we can usually use string names <lucide-icon name="user"></lucide-icon>
    // But let's stick to [img] referencing the object if the component supports it.
    // However, the issue might be the Module import.
    // Let's try importing the result of pick directly in imports if it returns a ModuleWithProviders.

    // CORRECT PATTERN FOR STANDALONE (per some versions):
    // imports: [LucideAngularModule, ...],
    // But actually, .pick returns a ModuleWithProviders which is for NgModule imports/providers.
    // For standalone components, we might need to import the LucideIconComponent directly if available?
    // No, standard docs say: import { LucideAngularModule, ... } ... imports: [LucideAngularModule.pick({...})]


    readonly icons = { User, Lock, AlertCircle, Loader, Layers };

    loginForm: FormGroup = this.fb.group({
        username: ['', Validators.required],
        password: ['', Validators.required],
        rememberMe: [false]
    });

    constructor() {
        const saved = localStorage.getItem('rememberedCredentials');
        if (saved) {
            try {
                const { username, password } = JSON.parse(saved);
                this.loginForm.patchValue({ username, password, rememberMe: true });
            } catch { }
        }
    }

    isLoggingIn = signal<boolean>(false);
    loginError = signal<boolean>(false);
    errorMessage = signal<string>('');

    doLogin() {
        if (this.loginForm.invalid) return;

        this.isLoggingIn.set(true);
        this.loginError.set(false);
        this.errorMessage.set('');

        const { username, password, rememberMe } = this.loginForm.value;

        if (rememberMe) {
            localStorage.setItem('rememberedCredentials', JSON.stringify({ username, password }));
        } else {
            localStorage.removeItem('rememberedCredentials');
        }

        // The input form has 'username' but API expects 'email'. 
        // Assuming the user enters email in the username field as per request example.
        this.authService.login(username, password).subscribe({
            next: () => {
                this.isLoggingIn.set(false);
                this.router.navigate(['/monitor']);
            },
            error: (err) => {
                console.error('Login error', err);
                this.isLoggingIn.set(false);
                this.loginError.set(true);

                // Extract error message from API response
                if (err.error?.errors && Array.isArray(err.error.errors) && err.error.errors.length > 0) {
                    this.errorMessage.set(err.error.errors[0].messageCustomer || err.error.errors[0].message || 'Falha na autenticação.');
                } else {
                    this.errorMessage.set('Usuário ou senha inválidos.');
                }
            }
        });
    }
}
